import { useIsCollectionsEnabled } from '@modules/common/hooks';
import useIsElementInScreenView from '@modules/common/hooks/useIsElementInScreenView';
import { Category } from '@modules/common/types';
import { getReasonOrEntityLabels } from '@modules/common/utils';
import FeedbackCardContent from '@modules/feedback/components/FeedbackCard/FeedbackCardContent';
import { isFeedbackSummaryPresent } from '@modules/feedback/components/FeedbackSummary/utils';
import {
  FeedbackListViewType,
  RECORDS_PER_PAGE,
} from '@modules/feedback/constants';
import { useIsSnippetOrFeedbackSaved } from '@modules/savedFeedbackList/hooks';
import { useSavedFeedbackListStore } from '@modules/savedFeedbackList/store/useSavedFeedbackListStore';
import { Paper, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import {
  Feedback,
  FeedbackLabel,
  FeedbackLabelType,
} from 'v2/core/entities/feedback';
import { SearchLabel } from 'v2/core/entities/feedback/labels';
import { FeedbackMetaData } from 'v2/store/useViewFeedbackStore';
import FeedbackCardHeader from './FeedbackCardHeader';
import { getLabelsToCategoryPairArray } from './FeedbackCategories/utils';
import { getSelectedLabels } from './utils';

const trackFeedbackPagination = createAnalyticsTracker('feedbackPagination');

interface FeedbackCardProps {
  feedback: Feedback;
  selectedFeedbackMetadata?: FeedbackMetaData[];
  selectedCategories?: Category[];
  feedbackIndex: number;
  feedbacks: Feedback[];
  isLoadingMore: boolean;
  variant: 'compact' | 'default';
  isSummaryView: boolean;
  feedbackListViewType: FeedbackListViewType;
  onViewDetailsButtonClick: (selectedLabels: FeedbackLabel[]) => void;
  isFeedbackDetailsDrawerVisible: boolean;
  setIsFeedbackDetailsDrawerVisible: (isVisible: boolean) => void;
  shouldShowRemoveFromCollectionButton?: boolean;
}

const FeedbackCard = ({
  feedback,
  selectedFeedbackMetadata: feedbackMetadata,
  selectedCategories,
  feedbackIndex,
  feedbacks,
  variant,
  isSummaryView,
  feedbackListViewType,
  onViewDetailsButtonClick,
  isFeedbackDetailsDrawerVisible,
  setIsFeedbackDetailsDrawerVisible,
  shouldShowRemoveFromCollectionButton = false,
}: FeedbackCardProps) => {
  const isCollectionsEnabled = useIsCollectionsEnabled();
  const [setFeedbackCardInViewRef] = useIsElementInScreenView({
    threshold: 0.1,
    triggerOnce: true,
    onChange: (inView) => {
      if (!inView) {
        return;
      }

      if (feedbackIndex && feedbackIndex % RECORDS_PER_PAGE === 0) {
        trackFeedbackPagination({
          eventName: 'Feedback Records Page Changed',
          page: feedbackIndex / RECORDS_PER_PAGE + 1,
          view: 'Feedback List',
        });
      }
    },
  });

  const isSavedFeedback = useIsSnippetOrFeedbackSaved({
    feedbackID: feedback.id,
  });

  const hasSavedSnippets = useIsSnippetOrFeedbackSaved({
    feedbackID: feedback.id,
    checkSavedSnippetsPresence: true,
  });

  const shouldShowSavedFeedbackColor =
    isSavedFeedback &&
    feedbackListViewType === FeedbackListViewType.DEFAULT &&
    !isCollectionsEnabled;

  const savedItemsMap = useSavedFeedbackListStore(
    ({ savedItemsMap }) => savedItemsMap,
  );

  const [selectedLabels, setSelectedLabels] = useState<FeedbackLabel[]>([]);

  const savedLabels = useMemo(() => {
    if (isCollectionsEnabled) {
      return (
        savedItemsMap[feedbacks[feedbackIndex]?.id]?.snippetMetadataV2?.reduce(
          (labels, metadata) => {
            if (!metadata.location.keywordSummary?.labelID) {
              return labels;
            }

            const label = {
              id: metadata.location.keywordSummary.labelID,
            } as FeedbackMetaData;
            labels.push(label);
            return labels;
          },
          [] as FeedbackMetaData[],
        ) ?? []
      );
    }

    return (
      savedItemsMap[feedbacks[feedbackIndex]?.id]?.snippetMetadata?.reduce(
        (labels, metadata) => {
          if (!metadata.labelID) {
            return labels;
          }

          const label = { id: metadata.labelID } as FeedbackMetaData;
          labels.push(label);
          return labels;
        },
        [] as FeedbackMetaData[],
      ) ?? []
    );
  }, [feedbackIndex, feedbacks, savedItemsMap, isCollectionsEnabled]);

  const selectedFeedbackMetadata = useMemo(
    () =>
      feedbackListViewType === FeedbackListViewType.DEFAULT
        ? feedbackMetadata
        : savedLabels.length
        ? savedLabels
        : feedbackMetadata,
    [feedbackMetadata, feedbackListViewType, savedLabels],
  );

  const selectedCategoryLabels = useMemo(() => {
    const reasonOrEntityLabels = getReasonOrEntityLabels(feedback.labels ?? []);

    const categoryLabels = getLabelsToCategoryPairArray({
      labels: reasonOrEntityLabels,
    });

    return categoryLabels
      .filter(([category]) => selectedCategories?.includes(category))
      .flatMap(([category, labels]) =>
        labels.map((label) => ({ id: label.id, category } as FeedbackMetaData)),
      );
  }, [feedback.labels, selectedCategories]);

  const updatedFeedbackMetadata = !selectedFeedbackMetadata?.[0]?.id
    ? selectedCategoryLabels
    : selectedFeedbackMetadata;

  const searchLabel = useMemo(
    () =>
      feedback.labels?.filter(
        (label): label is SearchLabel =>
          label.type === FeedbackLabelType.Search,
      )[0],
    [feedback.labels],
  );

  const isSummaryPresent = isFeedbackSummaryPresent(feedback);

  useEffect(() => {
    if (isFeedbackDetailsDrawerVisible) {
      return;
    }
    // 1. Hide the highlights on the preview feedback cards unless any entity
    // has been searched for.
    // 2. Hide the highlights on the feedback details drawer by default.
    // 3. Show the highlights on the feedback details drawer if the user
    // has selected a label or reason.
    const labels = getSelectedLabels(
      feedback.labels,
      updatedFeedbackMetadata,
      isSummaryPresent,
    );

    if (!!updatedFeedbackMetadata?.length) {
      setSelectedLabels(labels);
    }

    if (!!searchLabel?.matches?.length) {
      setSelectedLabels((prevState) => [...prevState, searchLabel]);
    }

    return () => {
      if (!isFeedbackDetailsDrawerVisible) {
        return;
      }
      setSelectedLabels([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    feedback,
    searchLabel,
    updatedFeedbackMetadata,
    isSummaryPresent,
    isFeedbackDetailsDrawerVisible,
  ]);

  const shouldHideUnsavedFeedback =
    feedbackListViewType === FeedbackListViewType.SAVED &&
    !isSavedFeedback &&
    !hasSavedSnippets &&
    !isCollectionsEnabled;

  return (
    <Paper
      ref={setFeedbackCardInViewRef}
      variant="outlined"
      sx={{
        borderRadius: 2,
        backgroundColor: shouldShowSavedFeedbackColor
          ? 'feedback.saved'
          : undefined,
        display: shouldHideUnsavedFeedback ? 'none' : undefined,
        mb: shouldHideUnsavedFeedback ? 0 : 2,
      }}
      elevation={shouldShowSavedFeedbackColor ? 0 : undefined}
    >
      <Stack>
        <FeedbackCardHeader
          feedback={feedback}
          onViewDetailsButtonClick={() => {
            onViewDetailsButtonClick(selectedLabels);
          }}
          variant={variant}
          isSummaryView={isSummaryView}
          feedbackListViewType={feedbackListViewType}
          shouldShowRemoveFromCollectionButton={
            shouldShowRemoveFromCollectionButton
          }
        />
        <FeedbackCardContent
          feedback={feedback}
          selectedLabels={selectedLabels}
          setSelectedLabels={setSelectedLabels}
          isFeedbackDetailsDrawerVisible={isFeedbackDetailsDrawerVisible}
          setIsFeedbackDetailsDrawerVisible={setIsFeedbackDetailsDrawerVisible}
          onViewDetailsButtonClick={onViewDetailsButtonClick}
          selectedFeedbackMetadata={updatedFeedbackMetadata}
          isSummaryView={isSummaryView}
          feedbackListViewType={feedbackListViewType}
        />
      </Stack>
    </Paper>
  );
};

export default FeedbackCard;
